import {ReactElement, useEffect} from 'react';
import React, {useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {RGBA} from '@Utils/color.util';
import {setCanvasCursor} from '@Utils/fabric.util';
import {deviceType, isAndroid, isFirefox, isIOS, isMacOs, isOpera, isSafari, isSamsungBrowser} from 'react-device-detect';
import Mousetrap from 'mousetrap';
import type * as Fabric from '@postermywall/fabricjs-2';
import styles from './canvas-color-picker.module.scss';
import {CURSOR_OVERLAY_CONTAINER_ID} from '@Components/poster-editor/components/cursor-overlay/cursor-overlay.types';
import type {CanvasEvents} from '@postermywall/fabricjs-2';

const DEVICE_TYPE = {
  MOBILE: 'mobile',
};

const CURSOR_OFFSET = 45;

interface CanvasColorPickerProps {
  color: RGBA;
  canvasRef?: Fabric.Canvas;
  canvasContainerRef?: JQuery;
  scale?: number;
  onChangeColor(color: RGBA | null): void;
  onChangeEnded(color: RGBA | null): void;
  className?: string;

  onChangeColorPickerMode?(state: boolean): void;
}

export function CanvasColorPicker({scale = 1, className = '', onChangeColorPickerMode = (): void => {}, ...props}: CanvasColorPickerProps): ReactElement<any> {
  const [isColorPickerEnabled, setIsColorPickerEnabled] = useState(false);
  const colorBeforePickerIsEnabled = useRef<RGBA>(undefined);
  const canvasScale = useRef<number>(scale);

  useEffect(() => {
    canvasScale.current = scale;
  }, [scale]);

  const onClickPicker = (): void => {
    if (!isColorPickerEnabled) {
      enableColorPicker();
    }
    setIsColorPickerEnabled(!isColorPickerEnabled);
    onChangeColorPickerMode(!isColorPickerEnabled);
  };

  const isCustomCursorPositioningUnavailable = (): boolean => {
    return (isSafari && (isIOS || isMacOs)) || isSamsungBrowser || isOpera || (isFirefox && isAndroid);
  }

  const getColorFromCoOrdinates = (e: CanvasEvents['mouse:move']): RGBA | null => {
    if (!props.canvasRef || !props.canvasContainerRef) {
      return null;
    }
    const pointer = e.scenePoint ?? e.viewportPoint;
    const left = props.canvasContainerRef.find('.canvas-container')[0].offsetLeft;
    const top = props.canvasContainerRef.find('.canvas-container')[0].offsetTop;
    const scaleForPointer = canvasScale.current * props.canvasRef.getRetinaScaling();
    const p = props.canvasRef.getContext().getImageData(pointer.x * scaleForPointer - left, pointer.y * scaleForPointer - top + (isCustomCursorPositioningUnavailable() ? CURSOR_OFFSET : 0), 1, 1).data;
    return [p[0], p[1], p[2], 1];
  };

  const onColorPicking = (e: CanvasEvents['mouse:move']): void => {
    const color = getColorFromCoOrdinates(e);
    props.onChangeColor(color);
  };

  const onColorPicked = (e: CanvasEvents['mouse:move']): void => {
    const color = getColorFromCoOrdinates(e);
    props.onChangeEnded(color);
    disableColorPickMode();
  };

  const onRevertColor = (): void => {
    if (colorBeforePickerIsEnabled.current) {
      props.onChangeColor(colorBeforePickerIsEnabled.current);
    }
  };

  const disableColorPickMode = (): void => {
    if (props.canvasRef && props.canvasContainerRef) {
      props.canvasRef.off('mouse:move', onColorPicking);
      props.canvasRef.off('mouse:down', onColorPicked);
      const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
      cursorOverlay?.removeEventListener('click', disableColorPickMode);
      props.canvasContainerRef.off('mouseout', onRevertColor);
      setCanvasCursor(props.canvasRef, 'default');
      props.canvasRef.setCursor('default');
      setIsColorPickerEnabled(false);
      onChangeColorPickerMode(false);
    }
  };

  const onPressEscapeOverloadKey = (): void => {
    if (isColorPickerEnabled) {
      disableColorPickMode();
      onRevertColor();
    }
  };

  const enableColorPicker = (): void => {
    if (props.canvasRef && props.canvasContainerRef) {
      colorBeforePickerIsEnabled.current = props.color;
      setCanvasCursor(props.canvasRef, `url("${window.PMW.util.asset_url('images/postermaker/color-picker.ico')}") 0 25 , auto`);
      props.canvasRef.setCursor(`url("${window.PMW.util.asset_url('images/postermaker/color-picker.ico')}") 0 25 , auto`);
      props.canvasRef.on('mouse:move', onColorPicking);
      props.canvasRef.on('mouse:down', onColorPicked);
      props.canvasContainerRef.on('mouseout', onRevertColor);
      const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
      cursorOverlay?.addEventListener('click', disableColorPickMode);

      if (deviceType !== DEVICE_TYPE.MOBILE) {
        Mousetrap.bind('escape', onPressEscapeOverloadKey);
      }
    }
  };

  return (
    <Icon
      onClick={onClickPicker}
      className={`${styles.pickerIcon} spacing-m-l-2`}
      icon="icon-colorpicker"
      size={IconSize.SIZE_ICON_20}
      shape={IconShape.SQUARE}
      type={IconType.SECONDARY}
    />
  );
}
